import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function PrivacyModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-bold text-gray-800">Politique de confidentialité — StageLink</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4">&times;</button>
        </div>

        <div className="text-sm text-gray-700 space-y-4">
          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Responsable du traitement</h3>
            <p>Lycée Alfred Nobel, Clichy-sous-Bois — représenté par le chef d'établissement.</p>
            <p>Outil développé et administré par Yacine HEURTELOUP, enseignant référent du projet de dématérialisation des conventions de stage.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Finalité du traitement</h3>
            <p>Les données collectées sont utilisées exclusivement dans le cadre de la gestion administrative des conventions de stage des apprenants du lycée Alfred Nobel (édition, transmission, signature et archivage des conventions).</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Données collectées</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Identité des apprenants (nom, prénom, classe, date de naissance)</li>
              <li>Identité des représentants légaux pour les apprenants mineurs (nom, prénom, email)</li>
              <li>Identité et coordonnées des tuteurs entreprise (nom, prénom, fonction, email)</li>
              <li>Coordonnées des entreprises d'accueil</li>
              <li>Documents contractuels générés (conventions de stage)</li>
              <li>Données de signature électronique (horodatage, adresse IP, audit trail)</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Base légale</h3>
            <p>Obligation légale — Article L124-1 et suivants du Code de l'éducation relatifs aux périodes de formation en milieu professionnel.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Hébergement</h3>
            <p>L'ensemble des données est hébergé sur un serveur situé en France, exploité au sein du lycée Alfred Nobel. Aucun transfert de données hors Union Européenne n'est effectué.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Outil de signature électronique</h3>
            <p>La signature électronique est assurée par DocuSeal, instance auto-hébergée sur le même serveur. Aucune donnée de signature n'est transmise à un tiers.</p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Durée de conservation</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Données de compte utilisateur : durée de scolarité ou d'exercice des fonctions + 1 an</li>
              <li>Conventions signées et audit trail : 1 an (un exemplaire papier étant conservé pour la durée légale de 5 ans)</li>
              <li>Données des entreprises et tuteurs : 3 ans après la fin de la relation</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Droits des personnes concernées</h3>
            <p>Conformément au RGPD (Règlement UE 2016/679), toute personne concernée dispose d'un droit d'accès, de rectification, d'effacement, de limitation et de portabilité de ses données.</p>
            <p>Pour exercer ces droits : <a href="mailto:yheurteloup@gmail.com" className="text-blue-600 hover:underline">yheurteloup@gmail.com</a></p>
            <p>En cas de réclamation non résolue : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.cnil.fr</a></p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-1">Mise à jour</h3>
            <p>Dernière mise à jour : mars 2026</p>
          </section>
        </div>

        <div className="mt-6 text-right">
          <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.tempPassword) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-600">StageLinks</h1>
          <p className="text-gray-500 mt-1">Gestion des conventions de stage</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Connexion</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="prenom.nom@etablissement.fr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
      <footer className="mt-6 text-center text-xs text-gray-400">
        <button onClick={() => setShowPrivacy(true)} className="hover:text-gray-600 hover:underline transition-colors">
          Politique de confidentialité
        </button>
      </footer>
    </div>
  );
}
